import { type MouseEvent, memo, type PointerEvent, useCallback } from 'react';

import { XIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { usePrivateWorkspaceAgentAssetListContext } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListProvider';
import { WORKSPACE_AGENT_ASSET_LIST_LABEL } from '~/components/v2/components/workspace/workspace-agent-asset-list/workspaceAgentAssetListConstants';
import { useOpenWorkspaceAgentAssetList } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetList';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';

const EMPTY_TAB_IDS: readonly string[] = [];

type AssetListTabProps = {
  id: string;
  isSelected: boolean;
  label: string;
  onClose: (artifactId: string) => void;
  onSelect?: (artifactId: string, title?: string) => void;
};

const AssetListTab = memo(({ id, isSelected, label, onClose, onSelect }: AssetListTabProps) => {
  const handleSelect = useCallback(() => {
    onSelect?.(id, label);
  }, [id, label, onSelect]);

  const handleClose = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onClose(id);
    },
    [id, onClose],
  );

  const handlePointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <div
      className={cn(
        'group relative z-10 flex h-8 w-36 max-w-full shrink-0 items-center gap-0.5 overflow-hidden rounded-t-6 border border-b-0 px-1.5',
        isSelected
          ? 'ads:border-default border-gray-4 ads:bg-base bg-gray-1 ads:text-primary text-gray-12'
          : 'border-transparent ads:text-secondary text-gray-10 hover:ads:bg-hover-subtle hover:bg-gray-3',
      )}
      data-testid="WORKSPACE_AGENT_ASSET_LIST_TAB"
    >
      <button
        aria-current={isSelected ? 'page' : undefined}
        className="min-w-0 flex-1 truncate text-left font-medium text-12"
        data-testid="WORKSPACE_AGENT_ASSET_LIST_TAB_SELECT"
        onClick={handleSelect}
        type="button"
      >
        {label}
      </button>
      <button
        aria-label={`Close ${label}`}
        className="relative z-20 flex size-5 shrink-0 items-center justify-center rounded-4 ads:text-icon-default text-gray-9 hover:ads:bg-hover-subtle hover:bg-gray-4 hover:ads:text-primary hover:text-gray-12"
        data-testid="WORKSPACE_AGENT_ASSET_LIST_TAB_CLOSE"
        onClick={handleClose}
        onPointerDown={handlePointerDown}
        title={`Close ${label}`}
        type="button"
      >
        <XIcon aria-hidden size="xs" />
      </button>
    </div>
  );
});

AssetListTab.displayName = 'AssetListTab';

export const PrivateWorkspaceAgentAssetListTabBar = memo(() => {
  const { artifactId, sessionId } = usePrivateWorkspaceAgentAssetListContext();
  const { closeAssetListTab, closeAssetListView, openAssetList } = useOpenWorkspaceAgentAssetList();
  const tabIds = useWorkspaceAgentAssetListViewStore((state) =>
    sessionId ? (state.tabsBySessionId[sessionId] ?? EMPTY_TAB_IDS) : EMPTY_TAB_IDS,
  );
  const titlesByArtifactId = useWorkspaceAgentAssetListViewStore((state) => state.titlesByArtifactId);

  const fallbackTabIds = artifactId ? [artifactId] : EMPTY_TAB_IDS;
  const resolvedTabIds = tabIds.length > 0 ? tabIds : fallbackTabIds;

  if (resolvedTabIds.length === 0) {
    return null;
  }

  return (
    <div
      className="relative flex h-9 w-full min-w-0 shrink-0 items-end gap-0.5 overflow-x-auto ads:bg-subtle bg-gray-2 px-2"
      data-testid="WORKSPACE_AGENT_ASSET_LIST_TAB_BAR"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 ads:border-default border-gray-4 border-b"
      />
      {resolvedTabIds.map((id) => (
        <AssetListTab
          id={id}
          isSelected={id === artifactId}
          key={id}
          label={titlesByArtifactId[id] || WORKSPACE_AGENT_ASSET_LIST_LABEL}
          onClose={closeAssetListTab}
          onSelect={openAssetList}
        />
      ))}
      <button
        aria-label="Close artifact view"
        className="sticky right-0 z-20 mb-1 ml-auto flex size-6 shrink-0 items-center justify-center rounded-4 ads:bg-subtle bg-gray-2 ads:text-icon-default text-gray-9 hover:ads:bg-hover-subtle hover:bg-gray-4 hover:ads:text-primary hover:text-gray-12"
        data-testid="WORKSPACE_AGENT_ASSET_LIST_VIEW_CLOSE"
        onClick={closeAssetListView}
        title="Close artifact view"
        type="button"
      >
        <XIcon aria-hidden size="xs" />
      </button>
    </div>
  );
});

PrivateWorkspaceAgentAssetListTabBar.displayName = 'PrivateWorkspaceAgentAssetListTabBar';
